import {HttpErrorResponse} from '@angular/common/http';
import {Component, Input, OnDestroy, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {Subject} from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import {
  DealPayrollArea, DealPayrollCoefficientUnit, DealPayrollGrade,
  DealPayrollGroupSettings, DealPayrollPosition, GROWTH_MODE
} from 'app/models/deal-payroll.model';
import {DealPayrollSettingsService} from 'app/services/deal-payroll-settings.service';
import {DECIMAL_ROUNDING_TOLERANCE} from 'app/models/deal-payroll-settings.constants';
import {CurrencyCode, PAYROLL_CURRENCY_CODES, trackByCurrencyCode}
  from 'app/models/payroll-currency.model';
import {
  DEAL_PAYROLL_GROUP_SETTINGS_ERROR_KEY, DEAL_PAYROLL_GROUP_SETTINGS_HTTP_STATUS,
  DEAL_PAYROLL_GROUP_SETTINGS_NEW_POSITION_PREFIX,
  DEAL_PAYROLL_GROUP_SETTINGS_TRACK_KEY_SEPARATOR
} from './deal-payroll-group-settings.constants';
import {DealPayrollGroupSettingsState} from './deal-payroll-group-settings.state';
import {DealPayrollNumberInputs, DealPayrollReferenceSalary} from './deal-payroll-group-settings.reference';
import {
  DealPayrollGroupSettingsFormPort, DealPayrollGroupSettingsRequestContext,
  DealPayrollGroupSettingsSaveContext, DealPayrollGroupSettingsViewState
} from './deal-payroll-group-settings.types';

export {DEAL_PAYROLL_GROUP_SETTINGS_SAVED_RESULT}
  from './deal-payroll-group-settings.constants';

const NUMBER_TYPE = 'number';
const FULL_PERCENT = 100;
const PERCENT_DECIMAL_PLACES = 2;
const WEIGHT_DECIMAL_PLACES = 4;
const PLANNED_WEIGHT_FIELD = 'plannedWeight';
const ZERO = 0;

@Component({
  selector: 'app-deal-payroll-group-settings',
  templateUrl: './deal-payroll-group-settings.component.html',
  styleUrls: ['./deal-payroll-group-settings.component.css']
})
export class DealPayrollGroupSettingsComponent implements OnDestroy {
  readonly growthModes = GROWTH_MODE;
  readonly currencies = PAYROLL_CURRENCY_CODES;
  readonly trackByCurrency = trackByCurrencyCode;
  readonly viewState: DealPayrollGroupSettingsViewState;
  readonly reference: DealPayrollReferenceSalary;
  readonly numbers = new DealPayrollNumberInputs();

  private state = new DealPayrollGroupSettingsState();
  private destroy$ = new Subject<void>();
  private initialWeightGrade: DealPayrollGrade = null;

  constructor(
    public activeModal: NgbActiveModal,
    private settingsService: DealPayrollSettingsService
  ) {
    this.viewState = this.state.viewState;
    this.reference = new DealPayrollReferenceSalary(() => this.viewState, () => this.draftChanged());
  }

  @ViewChild('settingsForm')
  set settingsForm(form: NgForm) {
    this.state.attachForm(form ? this.formPort(form) : null);
  }

  @Input()
  set groupId(value: string) {
    if (this.state.changeGroup(value)) { this.reference.reset(); this.numbers.blur(); this.load(); }
  }

  get groupId(): string { return this.state.groupId; }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(preserveError: boolean = false): void {
    this.plannedPercentBlur();
    const context = this.state.startLoading(preserveError);
    if (!context) { return; }
    this.settingsService.getDealPayrollGroupSettings(context.groupId)
      .takeUntil(this.destroy$).subscribe(
        response => { this.state.applyLoadedSettings(response, context); this.reference.reconcileSelection(); },
        error => this.rejectLoad(error, context));
  }

  retryLoad(): void {
    if (this.viewState.canRetryLoad) { this.load(); }
  }

  retryAreaCatalog(): void {
    const context = this.state.startAreaCatalogRefresh();
    if (!context) { return; }
    this.settingsService.getDealPayrollGroupSettings(context.groupId)
      .takeUntil(this.destroy$).subscribe(
        response => { this.state.applyAreaCatalogSettings(response, context); this.reference.reconcileSelection(); },
        error => this.rejectAreaCatalog(error, context));
  }

  save(): void {
    this.plannedPercentBlur();
    const context = this.state.startSaving();
    if (!context) { return; }
    this.settingsService.updateDealPayrollGroupSettings(context.groupId, context.request)
      .takeUntil(this.destroy$).subscribe(
        response => { this.state.applySavedSettings(response, context); this.reference.reconcileSelection(); },
        error => this.rejectSave(error, context));
  }

  restoreConflictDraft(): void {
    this.state.restoreConflict();
    this.reference.reconcileSelection();
  }
  addPosition(): void { this.state.addPosition(); }
  discardPosition(index: number): void { this.state.discardPosition(index); }
  gradeEnabledChanged(grade: DealPayrollGrade): void {
    this.state.gradeEnabledChanged(grade);
    this.reference.reconcileSelection();
  }
  draftChanged(): void { this.state.draftChanged(); }

  currencyChanged(position: DealPayrollPosition, currency: CurrencyCode): void {
    if (this.viewState.editingDisabled) { return; }
    position.rateCurrency = currency;
    position.plannedHourRate = null;
    position.actualHourRate = null;
    this.draftChanged();
  }

  get plannedPercent(): number {
    const grade = this.reference.grade;
    return grade ? this.weightPercent(this.hasPlannedWeightOverride
      ? grade.plannedWeight : this.viewState.settings.plannedWeight) : null;
  }
  get plannedPercentInput(): number | string {
    return this.percentInput(this.plannedPercent, PLANNED_WEIGHT_FIELD);
  }
  get hasPlannedWeightOverride(): boolean {
    return !!this.reference.grade && this.reference.grade.plannedWeight != null;
  }
  get actualPercent(): number {
    return this.plannedPercent == null ? null
      : Number((FULL_PERCENT - this.plannedPercent).toFixed(PERCENT_DECIMAL_PLACES));
  }
  plannedPercentChanged(value: number): void {
    if (this.reference.disabled) { return; }
    this.reference.grade.plannedWeight = typeof value === NUMBER_TYPE && isFinite(value) ? this.percentWeight(value) : NaN;
    if (this.viewState.settings.plannedWeight == null) { this.initialWeightGrade = this.reference.grade; }
    if (this.initialWeightGrade === this.reference.grade) {
      this.viewState.settings.plannedWeight = this.hasValidPercentScale(value) && value >= ZERO && value <= FULL_PERCENT
        ? this.reference.grade.plannedWeight : null;
    }
    this.draftChanged();
  }
  plannedPercentBlur(): void {
    this.initialWeightGrade = null;
    this.numbers.blur();
  }
  get monthlyNorm(): number {
    const norm = this.viewState.settings && this.viewState.settings.workingHoursPerMonth;
    return typeof norm === NUMBER_TYPE && isFinite(norm) && norm > ZERO ? norm : null;
  }

  close(): void {
    if (!this.viewState.saving) { this.activeModal.close(this.state.modalResult()); }
  }

  trackByPosition(index: number, position: DealPayrollPosition): string {
    return position.positionId || DEAL_PAYROLL_GROUP_SETTINGS_NEW_POSITION_PREFIX + index;
  }
  trackByGrade(_index: number, grade: DealPayrollGrade): string {
    return grade.gradeCode;
  }
  trackByArea(_index: number, area: DealPayrollArea): string {
    return area.directoryId + DEAL_PAYROLL_GROUP_SETTINGS_TRACK_KEY_SEPARATOR + area.areaId;
  }
  trackByUnit(_index: number, unit: DealPayrollCoefficientUnit): string {
    return unit.type + DEAL_PAYROLL_GROUP_SETTINGS_TRACK_KEY_SEPARATOR + unit.code;
  }
  trackByBlocker(_index: number, key: string): string { return key; }

  private weightPercent(weight: number): number {
    if (typeof weight !== NUMBER_TYPE || !isFinite(weight)) { return null; }
    const percent = weight * FULL_PERCENT;
    return this.hasValidPercentScale(percent) ? Number(percent.toFixed(PERCENT_DECIMAL_PLACES)) : percent;
  }

  private percentWeight(percent: number): number {
    const weight = percent / FULL_PERCENT;
    return this.hasValidPercentScale(percent) ? Number(weight.toFixed(WEIGHT_DECIMAL_PLACES)) : weight;
  }

  private percentInput(percent: number, field: string): number | string {
    return this.hasValidPercentScale(percent) ? this.numbers.display(percent, field) : percent;
  }

  private hasValidPercentScale(percent: number): boolean {
    const scaled = percent * FULL_PERCENT;
    return typeof percent === NUMBER_TYPE && isFinite(percent)
      && Math.abs(scaled - Math.round(scaled)) < DECIMAL_ROUNDING_TOLERANCE;
  }

  private rejectLoad(error: HttpErrorResponse,
                     context: DealPayrollGroupSettingsRequestContext): void {
    this.state.rejectLoad(context, this.loadErrorKey(error), this.blockerCodes(error));
  }

  private rejectAreaCatalog(error: HttpErrorResponse,
                            context: DealPayrollGroupSettingsRequestContext): void {
    this.state.rejectAreaCatalog(context, this.loadErrorKey(error));
  }

  private rejectSave(error: HttpErrorResponse,
                     context: DealPayrollGroupSettingsSaveContext): void {
    const conflict = this.state.rejectSave(
      context, this.saveErrorKey(error), this.blockerCodes(error));
    if (conflict) { this.load(true); }
  }

  private loadErrorKey(error: HttpErrorResponse): string {
    return error && error.status === DEAL_PAYROLL_GROUP_SETTINGS_HTTP_STATUS.FORBIDDEN
      ? DEAL_PAYROLL_GROUP_SETTINGS_ERROR_KEY.FORBIDDEN
      : DEAL_PAYROLL_GROUP_SETTINGS_ERROR_KEY.LOAD;
  }

  private saveErrorKey(error: HttpErrorResponse): string {
    if (error && error.status === DEAL_PAYROLL_GROUP_SETTINGS_HTTP_STATUS.CONFLICT) {
      return DEAL_PAYROLL_GROUP_SETTINGS_ERROR_KEY.STALE;
    }
    return error && error.status === DEAL_PAYROLL_GROUP_SETTINGS_HTTP_STATUS.FORBIDDEN
      ? DEAL_PAYROLL_GROUP_SETTINGS_ERROR_KEY.FORBIDDEN
      : DEAL_PAYROLL_GROUP_SETTINGS_ERROR_KEY.SAVE;
  }

  private blockerCodes(error: HttpErrorResponse): string[] {
    return error && error.error && error.error.blockers ? error.error.blockers : [];
  }

  private formPort(form: NgForm): DealPayrollGroupSettingsFormPort {
    return {isPristine: () => form.pristine,
      markAsDirty: () => form.form.markAsDirty(),
      markAsPristine: () => form.form.markAsPristine()};
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/view-group/deal-payroll-group-settings/deal-payroll-group-settings.component.ts